import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useVenueMarket } from "@/hooks/useVenueMarket";
import { Trophy, Sparkles, CheckCircle2, Lock } from "lucide-react";

const TIER_STYLE = {
  bronze:   { chip: "bg-amber-100 text-amber-900",   bar: "bg-amber-700",  glow: "from-amber-500/20"   },
  silver:   { chip: "bg-zinc-200 text-zinc-900",     bar: "bg-zinc-400",   glow: "from-zinc-400/20"    },
  gold:     { chip: "bg-yellow-100 text-yellow-900", bar: "bg-yellow-500", glow: "from-yellow-500/20"  },
  platinum: { chip: "bg-indigo-100 text-indigo-900", bar: "bg-indigo-600", glow: "from-indigo-500/20"  },
};

export default function Dividends() {
  const [status, setStatus] = useState(null);
  const { fmt } = useVenueMarket();

  useEffect(() => {
    http.get("/venues/me/dividends").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);

  if (!status) return <div className="p-10 text-sm text-zinc-500">Loading…</div>;

  const tier = status.current_tier;
  const next = status.next_tier;
  const tickets = status.tickets_since_baseline;
  const style = TIER_STYLE[tier.key] || TIER_STYLE.bronze;
  const spanLo = tier.min;
  const spanHi = tier.max ?? spanLo + 1;
  const pct = tier.max
    ? Math.min(100, Math.round(((tickets - spanLo) / (spanHi - spanLo)) * 100))
    : 100;
  const rate = (tier.rate_bp / 100).toFixed(tier.rate_bp % 100 === 0 ? 0 : 1);

  return (
    <div className="p-8 lg:p-10 max-w-4xl" data-testid="dividends-page">
      <div className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Rewards</div>
      <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl tracking-tight flex items-center gap-3">
        <Trophy className="w-8 h-8 text-indigo-600" /> Partner Dividends
      </h1>
      <p className="mt-2 text-zinc-500 max-w-2xl">
        The more tickets you sell, the more of our platform fee we share back with you — as a live cash dividend on every sale.
      </p>

      {/* Hero card */}
      <div className={`mt-8 rounded-2xl bg-zinc-900 text-white p-8 relative overflow-hidden`} data-testid="dividends-hero">
        <div className={`absolute inset-0 grid-bg opacity-[0.06]`} aria-hidden="true" />
        <div className={`absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gradient-to-br ${style.glow} to-transparent blur-3xl pointer-events-none`} aria-hidden="true" />
        <div className="relative">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${style.chip} text-[10px] font-mono uppercase tracking-widest`}>
              <Sparkles className="w-3 h-3" /> {tier.label} Tier
            </span>
          </div>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="font-display font-black text-6xl tracking-tighter" data-testid="dividends-total">
              {fmt(status.accumulated_dividend_pence)}
            </span>
            <span className="text-zinc-400 text-sm">earned so far</span>
          </div>
          <div className="mt-1 text-sm text-zinc-400">
            Currently earning <strong className="text-white">{rate}%</strong> of every booking fee, back to you.
          </div>

          <div className="mt-8 h-3 rounded-full bg-white/10 overflow-hidden">
            <div className={`h-full ${style.bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-zinc-400">
            <span>{tickets.toLocaleString()} tickets sold</span>
            {next ? (
              <span>{next.tickets_to_go.toLocaleString()} more to <strong className="text-white">{next.label}</strong> ({(next.rate_bp / 100).toFixed(next.rate_bp % 100 === 0 ? 0 : 1)}%)</span>
            ) : (
              <span className="text-white font-medium">You&apos;ve reached the top tier</span>
            )}
          </div>
        </div>
      </div>

      {/* Tier ladder */}
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dividends-tier-ladder">
        {status.all_tiers.map((t) => {
          const s = TIER_STYLE[t.key];
          const unlocked = tickets >= t.min;
          const current = t.key === tier.key;
          const tRate = (t.rate_bp / 100).toFixed(t.rate_bp % 100 === 0 ? 0 : 1);
          return (
            <div
              key={t.key}
              data-testid={`dividends-tier-${t.key}`}
              className={`rounded-xl p-5 border-2 transition ${
                current ? "border-indigo-600 bg-white shadow-lg"
                : unlocked ? "border-emerald-200 bg-emerald-50"
                : "border-zinc-200 bg-white opacity-70"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${s.chip} text-[10px] font-mono uppercase tracking-widest`}>
                  {t.label}
                </span>
                {unlocked ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Lock className="w-4 h-4 text-zinc-400" />
                )}
              </div>
              <div className="mt-3 font-display font-black text-3xl tracking-tighter text-zinc-900">
                {tRate}%
              </div>
              <div className="mt-1 text-xs text-zinc-500">
                {t.max
                  ? `${t.min.toLocaleString()} – ${t.max.toLocaleString()} tickets`
                  : `${t.min.toLocaleString()}+ tickets`}
              </div>
              {current && (
                <div className="mt-3 text-[11px] font-medium text-indigo-600 uppercase tracking-widest">
                  You are here
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* How it works */}
      <div className="mt-8 rounded-xl bg-zinc-50 border border-zinc-200 p-6">
        <div className="font-display font-bold text-zinc-900">How Partner Dividends work</div>
        <ul className="mt-3 space-y-2 text-sm text-zinc-600 leading-relaxed">
          <li>• Every time a ticket sells on Affra, we take a booking fee from the buyer.</li>
          <li>• We share a percentage of that fee back with you — the more tickets you sell lifetime, the higher the share.</li>
          <li>• Dividends accrue live as tickets are paid for. Payout mechanics are being finalised — for now, your balance is tracked and displayed.</li>
        </ul>
      </div>
    </div>
  );
}
